import type { VFC } from 'react';
import React from 'react';

import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { MustPreloadLoadableNames, mustPreloadWhenRenderingSPA } from '@confluence/loadable';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type { RendererExtensionHandlerProps } from './ExcerptExtensionHandler.tsx';
import { convertRendererProps, getMacroId } from './utils.ts';

export const DetailsExtensionHandler: VFC<RendererExtensionHandlerProps> = ({
	contentId,
	contentType,
	extensionKey,
	mode,
	node,
	spaceKey,
	appearance,
	showTemplateVariablesInPreview,
	showTemplateInputInPreview,
	Renderer,
	isLegacyRenderer,
	getExtensionHandlers,
	annotationProvider,
}) => {
	mustPreloadWhenRenderingSPA([MustPreloadLoadableNames.ClassicRendererLoader]);

	const renderServerPlaceholder = useRenderServerPlaceholder();
	const isHidden = Boolean(node?.parameters?.macroParams?.hidden?.value);
	const attributes = getMacroAttributesFromADFNode(node);
	const document = { type: 'doc', version: 1, content: node?.content };

	const { mode: contentMode } = useEditorContentModeValue({ contentId });
	const isCompactModeSupported =
		expValEquals('confluence_compact_text_format', 'isEnabled', true) ||
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		(expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
			fg('platform_editor_content_mode_button_mvp'));

	// ADFRenderer (next-based) requires extension handlers during rendering on server side,
	// for extensions like this as they use renderer internally. Handlers aren't needed with
	// legacy renderer because legacy component cooks them internally.
	const extensionHandlers =
		getExtensionHandlers && !isLegacyRenderer
			? getExtensionHandlers({
					adf: document,
					contentId,
					appearance,

					showTemplateInputInPreview,
					showTemplateVariablesInPreview,
				})
			: undefined;
	const macroId = getMacroId(node);

	return (
		<div
			data-fabric-macro={macroId}
			data-vc="page-properties-macro"
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': macroId }
				: { 'data-ssr-placeholder-replace': macroId })}
		>
			{isHidden ? null : (
				<Renderer
					{...convertRendererProps(isLegacyRenderer, { contentId, contentType, document })}
					showTemplateInputInPreview={showTemplateInputInPreview}
					showTemplateVariablesInPreview={showTemplateVariablesInPreview}
					extensionHandlers={extensionHandlers}
					appearance={appearance || 'default'}
					contentMode={isCompactModeSupported ? contentMode : undefined}
					spaceKey={spaceKey}
					UNSTABLE_allowTableResizing={false}
					annotationProvider={annotationProvider}
					allowAnnotations={!!annotationProvider}
					{...(editorExperiment('comment_on_bodied_extensions', true)
						? { allowInlineComments: true }
						: {})}
				/>
			)}
			<MacroExperienceSuccess
				name={getExperienceName(mode, node)}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
				contentId={contentId}
			/>
		</div>
	);
};
